import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';
import jobStatuses from '../constants/job-statuses';

function JobsAnalytics() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('month'); // 'day', 'week', 'month', 'quarter', 'year', 'custom'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedMonth, setSelectedMonth] = useState('2026-04');
  const [selectedYear, setSelectedYear] = useState('2026');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [stats, setStats] = useState({
    currentPeriod: {
      totalJobs: 0,
      activeJobs: 0,
      completedJobs: 0,
      overdueJobs: 0,
      completedBeforeDue: 0,
      firstTimeFix: 0,
      travelTime: 0,
      appointmentTime: 0,
      totalTime: 0,
      avgTravelTime: 0,
      avgAppointmentTime: 0,
      avgTotalTime: 0,
      totalValue: 0
    },
    previousPeriod: {
      totalJobs: 0,
      activeJobs: 0,
      completedJobs: 0,
      overdueJobs: 0,
      completedBeforeDue: 0,
      firstTimeFix: 0,
      travelTime: 0,
      appointmentTime: 0,
      totalTime: 0,
      avgTravelTime: 0,
      avgAppointmentTime: 0,
      avgTotalTime: 0,
      totalValue: 0
    },
    statusBreakdown: {},
    monthlyTrend: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, [viewMode, selectedDate, selectedMonth, selectedYear, customStartDate, customEndDate]);

  const getDateRange = () => {
    let currentStart, currentEnd, prevStart, prevEnd, label;
    const today = new Date();

    switch (viewMode) {
      case 'day':
        currentStart = new Date(selectedDate);
        currentStart.setHours(0, 0, 0, 0);
        currentEnd = new Date(selectedDate);
        currentEnd.setHours(23, 59, 59, 999);
        prevStart = new Date(currentStart);
        prevStart.setDate(prevStart.getDate() - 1);
        prevEnd = new Date(prevStart);
        prevEnd.setHours(23, 59, 59, 999);
        label = currentStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
        break;

      case 'week':
        currentStart = new Date(selectedDate);
        const dayOfWeek = currentStart.getDay();
        currentStart.setDate(currentStart.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1)); // Start on Monday
        currentStart.setHours(0, 0, 0, 0);
        currentEnd = new Date(currentStart);
        currentEnd.setDate(currentEnd.getDate() + 6);
        currentEnd.setHours(23, 59, 59, 999);
        prevStart = new Date(currentStart);
        prevStart.setDate(prevStart.getDate() - 7);
        prevEnd = new Date(currentEnd);
        prevEnd.setDate(prevEnd.getDate() - 7);
        label = `Week of ${currentStart.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}`;
        break;

      case 'month':
        const [year, month] = selectedMonth.split('-');
        currentStart = new Date(year, month - 1, 1);
        currentEnd = new Date(year, month, 0, 23, 59, 59, 999);
        prevStart = new Date(currentStart);
        prevStart.setMonth(prevStart.getMonth() - 1);
        prevEnd = new Date(prevStart.getFullYear(), prevStart.getMonth() + 1, 0, 23, 59, 59, 999);
        label = currentStart.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
        break;

      case 'quarter':
        const currentQuarter = Math.floor((today.getMonth()) / 3);
        currentStart = new Date(selectedYear, currentQuarter * 3, 1);
        currentEnd = new Date(selectedYear, currentQuarter * 3 + 3, 0, 23, 59, 59, 999);
        prevStart = new Date(currentStart);
        prevStart.setMonth(prevStart.getMonth() - 3);
        prevEnd = new Date(prevStart.getFullYear(), prevStart.getMonth() + 3, 0, 23, 59, 59, 999);
        label = `Q${currentQuarter + 1} ${selectedYear}`;
        break;

      case 'year':
        currentStart = new Date(selectedYear, 0, 1);
        currentEnd = new Date(selectedYear, 11, 31, 23, 59, 59, 999);
        prevStart = new Date(parseInt(selectedYear) - 1, 0, 1);
        prevEnd = new Date(parseInt(selectedYear) - 1, 11, 31, 23, 59, 59, 999);
        label = selectedYear;
        break;

      case 'custom':
        if (customStartDate && customEndDate) {
          currentStart = new Date(customStartDate);
          currentEnd = new Date(customEndDate);
          currentEnd.setHours(23, 59, 59, 999);
          const daysDiff = Math.floor((currentEnd - currentStart) / (1000 * 60 * 60 * 24));
          prevStart = new Date(currentStart);
          prevStart.setDate(prevStart.getDate() - daysDiff - 1);
          prevEnd = new Date(prevStart);
          prevEnd.setDate(prevEnd.getDate() + daysDiff);
          label = `${currentStart.toLocaleDateString('en-GB')} - ${currentEnd.toLocaleDateString('en-GB')}`;
        } else {
          return null;
        }
        break;

      default:
        return null;
    }

    return { currentStart, currentEnd, prevStart, prevEnd, label };
  };

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const dateRange = getDateRange();
      if (!dateRange) {
        setLoading(false);
        return;
      }

      const { currentStart, currentEnd, prevStart, prevEnd } = dateRange;

      // Fetch all jobs
      const jobsRes = await apiFetch('/api/jobs');
      const allJobs = await jobsRes.json();

      console.log('Total jobs fetched:', allJobs.length);
      console.log('Date range:', dateRange.label);

      // For now, just use all jobs regardless of date (since we might not have created_date)
      // In the future, filter by created_date when available
      const currentJobs = allJobs; // Use all jobs for now
      const previousJobs = []; // Empty for comparison

      console.log('Current period jobs:', currentJobs.length);
      console.log('Previous period jobs:', previousJobs.length);

      // Calculate metrics for current period
      const currentMetrics = calculateMetrics(currentJobs);
      const previousMetrics = calculateMetrics(previousJobs);

      // Calculate status breakdown
      const statusBreakdown = {};
      currentJobs.forEach(job => {
        const status = jobStatuses.labelFor(job.job_status);
        if (!statusBreakdown[status]) {
          statusBreakdown[status] = { count: 0, value: 0 };
        }
        statusBreakdown[status].count++;
        statusBreakdown[status].value += parseFloat(job.job_value || 0);
      });

      // Calculate 6-month trend
      const monthlyTrend = calculateMonthlyTrend(allJobs, currentStart);

      console.log('Status breakdown:', statusBreakdown);
      console.log('Monthly trend:', monthlyTrend);

      setStats({
        currentPeriod: currentMetrics,
        previousPeriod: previousMetrics,
        statusBreakdown,
        monthlyTrend
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setLoading(false);
    }
  };

  const calculateMetrics = (jobs) => {
    const completed = jobs.filter(j => jobStatuses.isCompleted(j.job_status));
    const overdue = jobs.filter(j => {
      if (!j.due_date || jobStatuses.isClosed(j.job_status)) return false;
      return new Date(j.due_date) < new Date();
    });
    const completedBeforeDue = completed.filter(j => {
      if (!j.due_date || !j.completed_date) return false;
      return new Date(j.completed_date) <= new Date(j.due_date);
    });

    return {
      totalJobs: jobs.length,
      activeJobs: jobs.filter(j => !jobStatuses.isClosed(j.job_status)).length,
      completedJobs: completed.length,
      overdueJobs: overdue.length,
      completedBeforeDue: completedBeforeDue.length,
      completedBeforeDuePercent: completed.length > 0 ? (completedBeforeDue.length / completed.length * 100).toFixed(1) : 0,
      firstTimeFix: 90.5, // Placeholder - would need first-time-fix tracking
      travelTime: 0, // Placeholder - would need time tracking
      appointmentTime: 0,
      totalTime: 0,
      avgTravelTime: 0,
      avgAppointmentTime: 0,
      avgTotalTime: 0,
      totalValue: jobs.reduce((sum, j) => sum + parseFloat(j.job_value || 0), 0)
    };
  };

  const calculateMonthlyTrend = (allJobs, currentMonth) => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const date = new Date(currentMonth);
      date.setMonth(date.getMonth() - i);
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      const monthJobs = allJobs.filter(job => {
        const createdDate = new Date(job.created_date);
        return createdDate >= monthStart && createdDate <= monthEnd;
      });

      months.push({
        label: date.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
        jobs: monthJobs.length,
        value: monthJobs.reduce((sum, j) => sum + parseFloat(j.job_value || 0), 0)
      });
    }
    return months;
  };

  const getTrendArrow = (current, previous) => {
    if (current > previous) return { icon: '↑', color: 'text-green-600' };
    if (current < previous) return { icon: '↓', color: 'text-red-600' };
    return { icon: '→', color: 'text-blue-600' };
  };

  const ComparisonCard = ({ title, currentCount, previousCount, currentValue, previousValue, valuePrefix = '£' }) => {
    const countTrend = getTrendArrow(currentCount, previousCount);
    const valueTrend = getTrendArrow(currentValue, previousValue);

    return (
      <div className="bg-white rounded-lg shadow p-4 border-t-4 border-blue-500">
        <h3 className="text-blue-600 font-semibold mb-3 text-sm">{title}</h3>
        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-600">This Month</span>
            <span className={`font-bold ${countTrend.color}`}>
              {currentCount} {countTrend.icon}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-gray-600">Last Month</span>
            <span className="font-semibold text-gray-700">{previousCount}</span>
          </div>
          {currentValue !== undefined && (
            <>
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-xs text-gray-600">This Month</span>
                <span className={`font-bold ${valueTrend.color}`}>
                  {valuePrefix}{currentValue.toLocaleString()} {valueTrend.icon}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-600">Last Month</span>
                <span className="font-semibold text-gray-700">
                  {valuePrefix}{previousValue.toLocaleString()}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const SummaryCard = ({ title, count, value }) => (
    <div className="bg-white rounded-lg shadow p-4 border-t-4 border-blue-500">
      <h3 className="text-blue-600 font-semibold mb-3 text-sm">{title}</h3>
      <div className="text-2xl font-bold text-gray-900">{count}</div>
      {value !== undefined && (
        <div className="text-sm text-gray-600 mt-1">£{value.toLocaleString()}</div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <PageHeader 
        title="Jobs Analytics"
        subtitle="Performance Metrics & Insights"
        backButton
        onBack={() => navigate('/dashboard')}
        backTitle="Back to Dashboard"
      />

      {/* Filter Bar */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-full mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-gray-600 font-semibold">
              {getDateRange()?.label || 'Select Date Range'}
            </div>
            <div className="text-sm text-gray-600">
              Today's Date - {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            {/* View Mode Selector */}
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {['day', 'week', 'month', 'quarter', 'year', 'custom'].map(mode => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-3 py-1 rounded text-sm font-medium transition ${
                    viewMode === mode
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {mode.charAt(0).toUpperCase() + mode.slice(1)}
                </button>
              ))}
            </div>

            {/* Date Inputs Based on View Mode */}
            {viewMode === 'day' && (
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
            {viewMode === 'week' && (
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
            {viewMode === 'month' && (
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            )}
            {viewMode === 'quarter' && (
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            )}
            {viewMode === 'year' && (
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - i).map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            )}
            {viewMode === 'custom' && (
              <>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  placeholder="Start Date"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  placeholder="End Date"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </>
            )}

            <div className="ml-auto text-xs text-gray-500">* All values are excluding VAT</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-full mx-auto px-6 py-6">
        
        {loading && (
          <div className="text-center py-12">
            <div className="text-4xl mb-4">⏳</div>
            <div className="text-gray-600">Loading analytics data...</div>
          </div>
        )}

        {!loading && (
          <>
        {/* Row 1 - Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <SummaryCard
            title="All Active Jobs"
            count={stats.currentPeriod.totalJobs}
            value={stats.currentPeriod.totalValue}
          />
          <SummaryCard
            title="Active Jobs - Current Month"
            count={stats.currentPeriod.activeJobs}
          />
          <SummaryCard
            title="Jobs Completed"
            count={stats.currentPeriod.completedJobs}
          />
          <SummaryCard
            title="Overdue Jobs"
            count={stats.currentPeriod.overdueJobs}
          />
        </div>

        {/* Row 2 - Comparison Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <ComparisonCard
            title="Active Jobs - This Month"
            currentCount={stats.currentPeriod.activeJobs}
            previousCount={stats.previousPeriod.activeJobs}
            currentValue={stats.currentPeriod.totalValue}
            previousValue={stats.previousPeriod.totalValue}
          />
          <ComparisonCard
            title="Jobs Completed Before Due Date"
            currentCount={stats.currentPeriod.completedBeforeDue}
            previousCount={stats.previousPeriod.completedBeforeDue}
            currentValue={stats.currentPeriod.completedBeforeDuePercent}
            previousValue={stats.previousPeriod.completedBeforeDuePercent}
            valuePrefix=""
          />
          <ComparisonCard
            title="First Time Fix"
            currentCount={stats.currentPeriod.firstTimeFix}
            previousCount={stats.previousPeriod.firstTimeFix}
            valuePrefix=""
          />
          <ComparisonCard
            title="Jobs Completed"
            currentCount={stats.currentPeriod.completedJobs}
            previousCount={stats.previousPeriod.completedJobs}
            currentValue={stats.currentPeriod.totalValue}
            previousValue={stats.previousPeriod.totalValue}
          />
        </div>

        {/* Row 3 - Charts and Additional Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Status Breakdown */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Jobs by Status</h3>
            <div className="space-y-3">
              {Object.entries(stats.statusBreakdown).map(([status, data]) => (
                <div key={status}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-medium text-gray-700">{status}</span>
                    <span className="font-bold text-gray-900">{data.count}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ width: `${(data.count / stats.currentPeriod.totalJobs) * 100}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pie Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Job Distribution</h3>
            <div className="relative w-48 h-48 mx-auto mb-4">
              <svg viewBox="0 0 100 100" className="transform -rotate-90">
                {Object.entries(stats.statusBreakdown).map((entry, idx) => {
                  const [status, data] = entry;
                  const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
                  const total = stats.currentPeriod.totalJobs;
                  const percentage = total > 0 ? (data.count / total) * 100 : 0;
                  const previousPercentages = Object.values(stats.statusBreakdown).slice(0, idx).reduce((sum, d) => sum + (d.count / total * 100), 0);
                  const startAngle = (previousPercentages / 100) * 360;
                  const angle = (percentage / 100) * 360;
                  const endAngle = startAngle + angle;
                  
                  const x1 = 50 + 40 * Math.cos((startAngle * Math.PI) / 180);
                  const y1 = 50 + 40 * Math.sin((startAngle * Math.PI) / 180);
                  const x2 = 50 + 40 * Math.cos((endAngle * Math.PI) / 180);
                  const y2 = 50 + 40 * Math.sin((endAngle * Math.PI) / 180);
                  
                  const largeArc = angle > 180 ? 1 : 0;
                  
                  return (
                    <path
                      key={status}
                      d={`M 50 50 L ${x1} ${y1} A 40 40 0 ${largeArc} 1 ${x2} ${y2} Z`}
                      fill={colors[idx % colors.length]}
                      className="hover:opacity-80 transition-opacity"
                    />
                  );
                })}
                <circle cx="50" cy="50" r="25" fill="white" />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="text-3xl font-bold text-gray-900">{stats.currentPeriod.totalJobs}</div>
                <div className="text-xs text-gray-600">Total Jobs</div>
              </div>
            </div>
            <div className="space-y-2">
              {Object.entries(stats.statusBreakdown).map((entry, idx) => {
                const [status, data] = entry;
                const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];
                return (
                  <div key={status} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded" style={{ backgroundColor: colors[idx % colors.length] }}></div>
                      <span className="text-gray-700">{status}</span>
                    </div>
                    <span className="font-bold text-gray-900">{data.count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Monthly Trend Bar Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">6-Month Trend</h3>
            <div className="space-y-3">
              {stats.monthlyTrend.map((month, idx) => {
                const maxJobs = Math.max(...stats.monthlyTrend.map(m => m.jobs), 1);
                const maxValue = Math.max(...stats.monthlyTrend.map(m => m.value), 1);
                return (
                  <div key={idx}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-gray-700">{month.label}</span>
                      <div className="flex gap-3">
                        <span className="text-blue-600 font-semibold">{month.jobs} jobs</span>
                        <span className="text-green-600 font-semibold">£{month.value.toLocaleString()}</span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div 
                            className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${(month.jobs / maxJobs) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      <div className="flex-1">
                        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                          <div 
                            className="bg-green-500 h-2 rounded-full transition-all duration-500"
                            style={{ width: `${(month.value / maxValue) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-center gap-6 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 text-xs">
                <div className="w-3 h-3 bg-blue-500 rounded"></div>
                <span className="text-gray-600">Job Count</span>
              </div>
              <div className="flex items-center gap-2 text-xs">
                <div className="w-3 h-3 bg-green-500 rounded"></div>
                <span className="text-gray-600">Total Value</span>
              </div>
            </div>
          </div>
        </div>
        </>
        )}

      </main>
    </div>
  );
}

export default JobsAnalytics;
